import { InnerBox } from "@/components/elements/InnerBox";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { divider, black, blue, red } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { FCC } from "@/types/common";
import { Stack, Box, Typography, TextField, IconButton, Paper, CircularProgress } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import dayjs from "dayjs";
import { styled } from "@mui/system";
import { useState, useEffect, useRef } from "react";
import { typography } from "@/theme/typography";
import { useParams } from "react-router-dom";
import { createTalentCommentApi, deleteTalentCommentApi, useTalentCommentsList } from "@/api/services/main/talent";
import Cookies from "js-cookie";
import { MONTH_DATE_FORMAT_WITH_TIMES } from "@/constants/datetime";
import { TCreateTalentCommentResDto } from "@/api/services/main/talent/dtos/create-talent-comment.dto";
import { transformComments } from "@/features/talent/utils/convertTalentComment";
import { TTalentComment } from "@/api/entities/talent-comment.entity";
import { UiComment } from "@/features/talent/types/talentCommentForm";
import { IconTrash } from "@/assets/icons/IconTrash";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { TextWithLinks } from "@/components/commons/TextWithLinks";
import { toast } from "@/utils/toast";
import { COMMENT_MAX_LENGTH_MESSAGE } from "@/constants/common";

const MAX_COMMENT_LENGTH = 1000;

type TTalentFormCommentProps = {
  defaultValues?: Partial<TTalentForm>;
};

interface CommentBubbleProps {
  isOwn?: boolean;
}

const CommentBubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "isOwn",
})<CommentBubbleProps>(({ isOwn }) => ({
  backgroundColor: isOwn ? blue[70] : black[99],
  color: isOwn ? "#fff" : black[30],
  padding: "8px",
  borderRadius: rounded.sm,
  width: "100%",
  fontFamily: "inherit",
  boxShadow: "none",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  whiteSpace: "pre-wrap",
  ...typography.body14Semi,
  "& a": {
    color: isOwn ? "#fff" : undefined,
    textDecoration: "underline",
  },
}));

export const TalentFormComment: FCC<TTalentFormCommentProps> = ({ defaultValues }) => {
  const { talentId } = useParams();
  const id = talentId ? Number(talentId) : defaultValues?.id ? defaultValues.id : 0;

  const userId = Cookies.get("userId");
  const isIrohana = Cookies.get("isMatchUser") === "false";

  const { data } = useTalentCommentsList(Number(id));

  const [comments, setComments] = useState<UiComment[]>([]);

  const [newComment, setNewComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [hoveredCommentId, setHoveredCommentId] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (data?.data) {
      const commentList = transformComments(data.data as TTalentComment[], Number(userId));
      setComments(commentList);
    }
  }, [data, userId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments]);

  const handleSend = async () => {
    if (!newComment.trim() || isSubmitting) return;
    if (newComment.length > MAX_COMMENT_LENGTH) {
      toast.error(COMMENT_MAX_LENGTH_MESSAGE(MAX_COMMENT_LENGTH));
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: newTalentComment }: TCreateTalentCommentResDto = await createTalentCommentApi(Number(id), {
        commentText: newComment,
      });

      setComments([
        ...comments,
        {
          id: newTalentComment.id || 0,
          name: newTalentComment.user?.fullName || "",
          text: newComment,
          time: dayjs(newTalentComment.createdAt).format(MONTH_DATE_FORMAT_WITH_TIMES),
          isOwn: true,
        },
      ]);
      setNewComment("");
      // The [comments] effect above scrolls to the bottom after this state update commits.
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (commentId: number) => {
    setDeleteConfirmId(commentId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId || deletingCommentId) return;

    setDeletingCommentId(deleteConfirmId);
    setDeleteConfirmId(null);
    try {
      await deleteTalentCommentApi(Number(id), deleteConfirmId);
      setComments(comments.filter((comment) => comment.id !== deleteConfirmId));
    } catch (error) {
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  return (
    <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box flex={1}>
          <Typography variant="sub16Bold" color={black[30]}>
            人材コメント
          </Typography>
        </Box>
      </Stack>

      <Stack
        ref={scrollRef}
        spacing={2}
        mt={3}
        sx={{
          maxHeight: 520,
          overflowY: "auto",
          scrollBehavior: "smooth",
        }}
      >
        {comments.map((comment) => (
          <Box key={comment.id} onMouseEnter={() => setHoveredCommentId(comment.id)} onMouseLeave={() => setHoveredCommentId(null)}>
            <Stack mb={0.5}>
              <Typography variant="body14Semi" color={black[70]}>
                {comment.name}
              </Typography>
            </Stack>
            <CommentBubble elevation={0} isOwn={comment.isOwn}>
              <TextWithLinks text={comment.text} />
            </CommentBubble>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mt={0.5}>
              <Typography variant="cap12" color={black[90]}>
                {comment.time}
              </Typography>
              {comment.isOwn && (hoveredCommentId === comment.id || deletingCommentId === comment.id) && (
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(comment.id)}
                  disabled={deletingCommentId === comment.id}
                  sx={{
                    padding: "2px",
                    color: black[70],
                    "&:hover": {
                      color: red[70],
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  {deletingCommentId === comment.id ? (
                    <CircularProgress size={20} sx={{ color: black[80] }} />
                  ) : (
                    <IconTrash fontSize={14} color={red[70]} />
                  )}
                </IconButton>
              )}
            </Stack>
          </Box>
        ))}
      </Stack>

      <Box sx={{ borderTop: `1px solid ${divider.high}`, mt: 2, pt: 2, mx: -3 }}>
        <Stack direction="row" spacing={1} alignItems="flex-end" px={3}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="入力してください"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            disabled={isSubmitting || isIrohana}
            multiline
            minRows={1.2}
            maxRows={6}
            sx={{
              fontFamily: "inherit",
              backgroundColor: isSubmitting || isIrohana ? black[99] : "#FFF",
              borderRadius: rounded.sm,
              "& .MuiOutlinedInput-root": {
                borderRadius: rounded.sm,
                padding: "8px 12px",
                "&.Mui-focused fieldset": {
                  borderColor: blue[80],
                },
                "&.Mui-disabled": {
                  borderRadius: rounded.sm,
                  backgroundColor: black[99],
                  "& fieldset": {
                    borderRadius: rounded.sm,
                    borderColor: black[99],
                  },
                },
              },
              "& .MuiOutlinedInput-input": {
                padding: 0,
                ...typography.body14Semi,
              },
              "& .MuiOutlinedInput-input.Mui-disabled": {
                color: black[70],
                borderRadius: rounded.sm,
                WebkitTextFillColor: black[70],
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={isSubmitting || !newComment.trim() || isIrohana}
            sx={{
              backgroundColor: isSubmitting || !newComment.trim() || isIrohana ? black[99] : "#FFF",
              color: isSubmitting || !newComment.trim() || isIrohana ? black[70] : blue[80],
              border: isSubmitting || !newComment.trim() || isIrohana ? "2px solid #e0e0e0" : `2px solid ${blue[80]}`,
              borderRadius: rounded.sm,
              padding: "7px 17px",
              flexShrink: 0,
              "&:hover": {
                backgroundColor: isSubmitting || !newComment.trim() || isIrohana ? black[99] : "#f5f5f5",
              },
              "&.Mui-disabled": {
                backgroundColor: black[99],
                color: black[70],
                border: `2px solid ${black[99]}`,
                opacity: 1,
              },
            }}
          >
            {isSubmitting ? <CircularProgress size={24} sx={{ color: black[70] }} /> : <SendIcon />}
          </IconButton>
        </Stack>
      </Box>

      <ConfirmDeleteModal
        open={deleteConfirmId !== null}
        onClose={handleDeleteCancel}
        onOk={handleDeleteConfirm}
        message="このコメントを削除してもよろしいですか？"
        title=""
      />
    </InnerBox>
  );
};
